import React, { useContext, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Chip,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import UserContextProvider from '../../store/UserContext';
import { AppContext } from '../../store/AppContext';
import NavUser from './NavUser';
import { Link } from 'react-router-dom';
import { withRouter } from 'react-router';
import {
  Apartment,
  Assessment,
  AutoAwesome,
  CalendarMonth,
  CreditCard,
  Dashboard,
  Description,
  HourglassTopRounded,
  KeyboardArrowLeft,
  Menu,
  People,
  PieChart,
  Settings,
  SupervisedUserCircle,
} from '@mui/icons-material';
import logo from '../../assets/img/logo.png';
import { baseURL, GetRejectedReportCount, GetSubmittedReportCount } from '../../helpers/api';
import { activityStatus, userCats } from '../../helpers/appstate';
import useSWR from 'swr';

const Layout = withRouter(({ history, children, headerTitle }) => {
  // --- CORE LOGIC (UNTOUCHED) ---
  const { state } = useContext(AppContext);
  const { userCategory, userPermission, status } = state.auth.result.data;
  console.log(state);

  const permissions = userPermission.flatMap((i) => {
    return i.permissionActivity.map((i2) => i2.privilegeCode.toLowerCase());
  });

  const { pathname } = history?.location;
  const currPage = pathname.split('/')[1];
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Pending accounts see no records until an admin activates them (Settings stays available)
  const isPendingUser = status === activityStatus.PENDING;
  const pendingAllowedPaths = ['/settings', '/settings/profile'];
  const isPageBlocked =
    isPendingUser && !pendingAllowedPaths.includes(pathname.replace(/\/+$/, '') || '/');

  const canViewReports =
    permissions.includes('view_report') || permissions.includes('view_comparative_report');
  const canViewSchedules = permissions.includes('schedule_quarter');
  const canViewMonitoring =
    permissions.includes('activity_reports') ||
    permissions.includes('monitoring_reports') ||
    permissions.includes('payment_reports');
  // const canAskAi = userCategory === userCats.EDM || userCategory === userCats.REGULATOR;
  const canAskAi = true;
  const canViewFacility =
    permissions.includes('view_facility') ||
    permissions.includes('create_facility') ||
    permissions.includes('update_facility');
  const canViewUsers = permissions.includes('view_users') || permissions.includes('user_invite');
  const canViewPayment = permissions.includes('view_payment');
  const canViewInvoice = permissions.includes('view_invoice');

  const fetchSubmittedResultCount = async () => {
    const res = await GetSubmittedReportCount();
    const { data, status } = res?.result || {};
    if (status?.code === '00') return data;
    throw new Error('Failed to fetch count');
  };

  const fetchRejectedResultCount = async () => {
    const res = await GetRejectedReportCount();
    const { data, status } = res?.result || {};
    if (status?.code === '00') return data;
    throw new Error('Failed to fetch count');
  };

  const { data: submittedResultCount } = useSWR(
    isPendingUser ? null : 'submitted-report-count',
    fetchSubmittedResultCount,
    {
      refreshInterval: 2000,
      revalidateOnFocus: false,
    }
  );

  const { data: rejectedResultCount } = useSWR(
    isPendingUser ? null : 'rejected-report-count',
    fetchRejectedResultCount,
    {
      refreshInterval: 2000,
      revalidateOnFocus: false,
    }
  );

  // --- NAVIGATION CONFIGURATION ---
  const links = [
    {
      icon: <Dashboard fontSize="small" />,
      name: 'Dashboard',
      url: '/',
      visible: true,
      group: 'analytics',
    },
    {
      icon: <PieChart fontSize="small" />,
      name: 'Results',
      url: '/results',
      visible:
        (userCategory === userCats.PERSONNEL || canViewReports) && status === activityStatus.ACTIVE,
      group: 'analytics',
      more: () => {
        const totalAlerts = (submittedResultCount || 0) + (rejectedResultCount || 0);
        if (totalAlerts > 0 && userCategory === userCats.EDM) {
          return (
            <Chip
              label={totalAlerts}
              size="small"
              sx={{
                bgcolor: '#ff4842',
                color: '#fff',
                fontWeight: 700,
                height: 22,
                minWidth: 22,
                borderRadius: '8px',
              }}
            />
          );
        }
        return null;
      },
    },
    {
      icon: <Assessment fontSize="small" />,
      name: 'Reports',
      url: '/reporting',
      visible:
        (userCategory === userCats.PERSONNEL ||
          userCategory === userCats.CONSULTANT ||
          canViewMonitoring) &&
        status === activityStatus.ACTIVE,
      group: 'analytics',
    },
    {
      icon: <AutoAwesome fontSize="small" />,
      name: 'Ask AI',
      url: '/ask-ai',
      visible: canAskAi && status === activityStatus.ACTIVE,
      group: 'analytics',
      group: 'analytics',
    },
    {
      icon: <Apartment fontSize="small" />,
      name: 'Facilities',
      url: '/facilities',
      visible:
        (userCategory === userCats.PERSONNEL || canViewFacility) &&
        status === activityStatus.ACTIVE,
      group: 'activities',
    },
    {
      icon: <CalendarMonth fontSize="small" />,
      name: 'QAQC Planner',
      url: '/schedules',
      visible:
        userCategory === userCats.EDM && canViewSchedules && status === activityStatus.ACTIVE,
      group: 'activities',
    },
    {
      icon: <Description fontSize="small" />,
      name: 'Invoices',
      url: '/invoices',
      visible:
        (userCategory === userCats.PERSONNEL || canViewInvoice) && status === activityStatus.ACTIVE,
      group: 'transactions',
    },
    {
      icon: <CreditCard fontSize="small" />,
      name: 'Payments',
      url: '/payments',
      visible:
        (userCategory === userCats.PERSONNEL || canViewPayment) && status === activityStatus.ACTIVE,
      group: 'transactions',
    },
    {
      icon: <People fontSize="small" />,
      name: 'Users',
      url: '/users',
      visible: userCategory === userCats.EDM && canViewUsers && status === activityStatus.ACTIVE,
      group: 'manage',
    },
    {
      icon: <SupervisedUserCircle fontSize="small" />,
      name: 'Consultants',
      url: '/consultants',
      visible: userCategory === userCats.EDM && canViewUsers && status === activityStatus.ACTIVE,
      group: 'manage',
    },
    {
      icon: <Settings fontSize="small" />,
      name: 'Settings',
      url: '/settings',
      visible: true,
      group: 'manage',
    },
  ];

  const linkGroup = [
    { name: 'analytics', visible: true },
    {
      name: 'activities',
      visible:
        (userCategory === userCats.PERSONNEL || canViewFacility || canViewSchedules) &&
        status === activityStatus.ACTIVE,
    },
    {
      name: 'transactions',
      visible:
        (userCategory === userCats.PERSONNEL || canViewPayment || canViewInvoice) &&
        status === activityStatus.ACTIVE,
    },
    { name: 'manage', visible: true },
  ];

  const isTest = process.env.REACT_APP_ENV === 'test';
  const drawerWidth = 260; // Slightly wider for modern feel
  const initDrawerOpen = !smallScreen;
  const [drawerOpen, setDrawerOpen] = useState(initDrawerOpen);

  return (
    <UserContextProvider>
      <CssBaseline />

      {/* 1. MODERN APP BAR (Glassmorphism) */}
      <AppBar
        elevation={0}
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: alpha('#ffffff', 0.9), // Translucent white
          backdropFilter: 'blur(8px)', // Glass effect
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: theme.zIndex.drawer - 1,
        }}
      >
        <Toolbar sx={{ height: '9vh', minHeight: '70px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              {smallScreen && (
                <IconButton
                  onClick={() => setDrawerOpen(!drawerOpen)}
                  sx={{ mr: 1, color: 'text.secondary' }}
                >
                  <Menu />
                </IconButton>
              )}
              <Typography
                variant="h6"
                color="text.primary"
                sx={{ fontWeight: '700', textTransform: 'capitalize' }}
              >
                {headerTitle}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {isTest && (
                <Chip
                  label="Test Environment"
                  color="error"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              )}
              <NavUser />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* 2. MODERN DRAWER (Sidebar) */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        variant={smallScreen ? 'temporary' : 'persistent'}
        PaperProps={{
          sx: {
            width: drawerWidth,
            backgroundColor: '#0c5bab',
            color: '#ffffff',
            borderRight: 'none',
            boxShadow: '4px 0 24px rgba(0,0,0,0.05)',
          },
        }}
      >
        <Box
          sx={{ height: '9vh', minHeight: '70px', display: 'flex', alignItems: 'center', px: 3 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
            <Box component="img" src={logo} alt="iems logo" sx={{ height: 32, width: 32 }} />
            <Typography
              sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '1.25rem', letterSpacing: 1 }}
            >
              I.E.M.S
            </Typography>
          </Box>
          {smallScreen && (
            <IconButton sx={{ color: '#FFF' }} onClick={() => setDrawerOpen(!drawerOpen)}>
              <KeyboardArrowLeft />
            </IconButton>
          )}
        </Box>

        {/* --- CUSTOM THIN SCROLLBAR APPLIED HERE --- */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            px: 2,
            pb: 4,
            // Scrollbar styling for Webkit browsers (Chrome, Safari, Edge)
            '&::-webkit-scrollbar': {
              width: '6px', // Make it thin
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent', // Hide the track so it looks clean
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha('#ffffff', 0.2), // Soft translucent white thumb
              borderRadius: '10px', // Rounded edges
              '&:hover': {
                backgroundColor: alpha('#ffffff', 0.4), // Gets slightly brighter when hovered
              },
            },
            // Fallback for Firefox
            scrollbarWidth: 'thin',
            scrollbarColor: `${alpha('#ffffff', 0.2)} transparent`,
          }}
        >
          {linkGroup
            .filter((x) => x.visible)
            .map((grp, grpIdx) => (
              <List
                key={grpIdx}
                subheader={
                  <Typography
                    variant="caption"
                    sx={{
                      px: 2,
                      mt: 2,
                      mb: 1,
                      display: 'block',
                      textTransform: 'uppercase',
                      fontWeight: 700,
                      letterSpacing: 1.2,
                      color: alpha('#ffffff', 0.5),
                    }}
                  >
                    {grp.name}
                  </Typography>
                }
                sx={{ mb: 1 }}
              >
                {links
                  .filter((x) => x.group === grp.name)
                  .map((link, index) => {
                    if (!link.visible) return null;

                    const url = link.url.split('/')[1];
                    const isActive = currPage === url;

                    return (
                      <ListItemButton
                        key={index}
                        component={Link}
                        to={link.url}
                        onClick={() => smallScreen && setDrawerOpen(false)}
                        sx={{
                          mb: 0.5,
                          borderRadius: '10px',
                          bgcolor: isActive ? alpha('#ffffff', 0.15) : 'transparent',
                          color: isActive ? '#ffffff' : alpha('#ffffff', 0.8),
                          '&:hover': {
                            bgcolor: isActive ? alpha('#ffffff', 0.2) : alpha('#ffffff', 0.08),
                            color: '#ffffff',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                          {link.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={link.name}
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontWeight: isActive ? 700 : 500,
                          }}
                        />
                        {link.more && link.more()}
                      </ListItemButton>
                    );
                  })}
              </List>
            ))}
        </Box>
      </Drawer>

      {/* 3. MAIN CONTENT WRAPPER */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          mt: '9vh', // Replaces the old <br/> tags. Pushes content below the fixed header perfectly.
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isPageBlocked ? (
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              bgcolor: '#F4F6F8',
            }}
          >
            <Box
              sx={{
                maxWidth: 480,
                textAlign: 'center',
                p: { xs: 3, md: 5 },
                bgcolor: '#fff',
                borderRadius: '20px',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  mx: 'auto',
                  mb: 2.5,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha('#ed6c02', 0.1),
                  color: '#ed6c02',
                }}
              >
                <HourglassTopRounded sx={{ fontSize: 32 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Your account is awaiting activation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                You'll be able to view records once an administrator has activated your account.
                Please check back later or contact your administrator if this is taking longer than
                expected.
              </Typography>
              <br />
              <Button
                component={Link}
                to="/settings"
                variant="outlined"
                startIcon={<Settings />}
                sx={{ borderRadius: '8px' }}
              >
                Go to settings
              </Button>
            </Box>
          </Box>
        ) : (
          children
        )}
      </Box>
    </UserContextProvider>
  );
});

export default Layout;
